import { useEffect, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { usePagedList } from "@/lib/usePagedList";
import LoadMoreButton from "@/components/LoadMoreButton";
import JobCard from "@/components/JobCard";
import SeoHead from "@/components/SeoHead";
import PageHero from "@/components/PageHero";
import { jobOptions } from "@/lib/jobLabels";

const Jobs = () => {
  const { t, lang } = useLang();
  const [filters, setFilters] = useState({
    country: "all", contract_type: "all", seniority: "all", work_mode: "all",
    time_type: "all", salary_range: "all", technology: "all", since: "all", q: "",
  });
  const [techs, setTechs] = useState([]);

  useEffect(() => {
    api.get("/jobs/technologies").then((r) => setTechs(r.data || [])).catch(() => {});
  }, []);

  const { items, loading, loadingMore, hasMore, loadMore } = usePagedList("/jobs", { ...filters, language: lang });

  const allOpt = { value: "all", label: t.filters.all };
  const Selector = ({ label, k, options, testid }) => (
    <label className="flex flex-col gap-1 text-[12px]">
      <span className="dl-overline">{label}</span>
      <select
        data-testid={testid}
        value={filters[k]}
        onChange={(e) => setFilters({ ...filters, [k]: e.target.value })}
        className="bg-white border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:border-[#111827]"
      >
        {[allOpt, ...options].map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );

  const periodOpts = [
    { value: "today", label: t.filters.periodToday },
    { value: "week", label: t.filters.periodWeek },
    { value: "month", label: t.filters.periodMonth },
    { value: "quarter", label: t.filters.periodQuarter },
    { value: "year", label: t.filters.periodYear },
  ];

  return (
    <div data-testid="jobs-page">
      <SeoHead
        lang={lang}
        path="/jobs"
        title={lang === "fr" ? "Offres d'emploi Supply Chain IA" : "Supply Chain AI jobs"}
        description={lang === "fr"
          ? "Postes IA en Supply Chain, planification, S&OP et data. Signaux de marché et opportunités curées par Dataleo pour les Opérations."
          : "AI roles in Supply Chain, planning, S&OP and data. Market signals and curated opportunities from Dataleo for Operations leaders."}
      />
      <PageHero
        kicker={lang === "fr" ? "Flux · Emplois" : "Feed · Jobs"}
        title={t.nav.jobs}
        description={lang === "fr" ? "Signaux de marché à travers les recrutements IA en Opérations." : "Market signals from hires in AI for Operations."}
        accent="green"
        testId="jobs-hero"
      />
      <div className="dl-container py-10">
      <div className="flex flex-wrap items-end gap-4 mb-8">
        <Selector testid="job-filter-country" label={t.filters.country} k="country" options={jobOptions("country", lang)} />
        <Selector testid="job-filter-contract" label={lang === "fr" ? "Type de contrat" : "Contract type"} k="contract_type" options={jobOptions("contract_type", lang)} />
        <Selector testid="job-filter-seniority" label={t.filters.seniority} k="seniority" options={jobOptions("seniority", lang)} />
        <Selector testid="job-filter-workmode" label={t.filters.workMode} k="work_mode" options={jobOptions("work_mode", lang)} />
        <Selector testid="job-filter-time" label={lang === "fr" ? "Temps de travail" : "Working time"} k="time_type" options={jobOptions("time_type", lang)} />
        <Selector testid="job-filter-salary" label={lang === "fr" ? "Salaire" : "Salary"} k="salary_range" options={jobOptions("salary_range", lang)} />
        <Selector testid="job-filter-tech" label={lang === "fr" ? "Technologie" : "Technology"} k="technology" options={techs.map((v) => ({ value: v, label: v }))} />
        <Selector testid="job-filter-period" label={t.filters.period} k="since" options={periodOpts} />
        <button
          data-testid="job-filter-reset"
          onClick={() => setFilters({ country: "all", contract_type: "all", seniority: "all", work_mode: "all", time_type: "all", salary_range: "all", technology: "all", since: "all", q: "" })}
          className="text-sm underline-offset-4 hover:underline text-[#4B5563]"
        >{t.filters.reset}</button>
      </div>
      {!loading && items.length === 0 && (
        <div data-testid="jobs-empty" className="text-[#4B5563] py-12 text-center">
          {lang === "fr" ? "Aucun résultat." : "No results."}
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-6" data-testid="jobs-grid">
        {items.map((j, i) => <JobCard key={j.id} item={j} idx={i} />)}
      </div>
      {!loading && <LoadMoreButton hasMore={hasMore} loading={loadingMore} onClick={loadMore} lang={lang} testid="jobs-load-more" />}
      </div>
    </div>
  );
};

export default Jobs;
